import { useMemo, useState } from "react";

import { ArrowDownOnSquareIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import useVehicleComplexityRules from "../../data/useVehicleComplexityRules";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { useAuth } from "../../contexts/AuthContext";
import {
  COMPLEXITY_CLASSES,
  COMPLEXITY_LABELS,
  IVehicleComplexityRule,
} from "../../models/VehicleComplexity";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";
import Tooltip from "../shared/Tooltip";
import VehicleComplexityRuleModal from "./VehicleComplexityRuleModal";

/** One matrix row: a make/model pairing, with whichever sites have graded it. */
interface MatrixRow {
  key: string;
  make: string | null;
  model: string | null;
  specificity: number;
  byCustomerId: Record<string, IVehicleComplexityRule>;
}

/**
 * Blank means "any", and it has to be visibly different from a site simply not having graded this
 * row — otherwise an empty cell and an "any make" rule look the same.
 */
const ANY = "Any";

export const matrixKey = (make: string | null, model: string | null) =>
  `${(make ?? "").trim().toLowerCase()}|${(model ?? "").trim().toLowerCase()}`;

export default function VehicleComplexityMatrix() {
  const { t } = useTranslation("vehicles");
  const { customerId } = useAuth();
  const { rules, createRule, updateRule, deleteRule } = useVehicleComplexityRules();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers(t("Complexity rule"));

  const [editing, setEditing] = useState<IVehicleComplexityRule | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [prefill, setPrefill] = useState<{ make: string | null; model: string | null } | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<IVehicleComplexityRule | undefined>(undefined);
  const [adoptingKey, setAdoptingKey] = useState<string | null>(null);

  // Memoised because both the column and row builders depend on it — a fresh [] fallback on every
  // render would make them recompute the whole matrix each time.
  const allRules = useMemo(() => rules.data?.data ?? [], [rules.data]);

  /**
   * Sites become columns. Ours goes first whether or not we have any rules yet — a site with an
   * empty list is exactly who most needs to see the others and adopt from them.
   */
  const customers = useMemo(() => {
    const map = new Map<string, string>();

    if (customerId) map.set(customerId, t("Your site"));

    for (const rule of allRules) {
      if (rule.customerId === customerId) continue;
      if (!map.has(rule.customerId)) map.set(rule.customerId, rule.customerName ?? t("Unknown site"));
    }

    return Array.from(map, ([id, name]) => ({ id, name, isMine: id === customerId }));
  }, [allRules, customerId, t]);

  const rows = useMemo(() => {
    const map = new Map<string, MatrixRow>();

    for (const rule of allRules) {
      const key = matrixKey(rule.make, rule.model);
      const existing = map.get(key);

      if (existing) {
        existing.byCustomerId[rule.customerId] = rule;
        continue;
      }

      map.set(key, {
        key,
        make: rule.make,
        model: rule.model,
        specificity: rule.specificity,
        byCustomerId: { [rule.customerId]: rule },
      });
    }

    // Most specific first, matching the order the matcher itself applies them in, so reading down the
    // page is reading the rules in priority order.
    return Array.from(map.values()).sort(
      (a, b) =>
        b.specificity - a.specificity ||
        (a.make ?? "").localeCompare(b.make ?? "") ||
        (a.model ?? "").localeCompare(b.model ?? "")
    );
  }, [allRules]);

  const openCreate = (seed?: { make: string | null; model: string | null }) => {
    setEditing(undefined);
    setPrefill(seed);
    setModalOpen(true);
  };

  const openEdit = (rule: IVehicleComplexityRule) => {
    setEditing(rule);
    setPrefill(undefined);
    setModalOpen(true);
  };

  /**
   * Copy another site's grading onto our own list in one click. Creates our rule when we haven't got
   * one for this make/model, otherwise updates the grading on the rule we already have — the same
   * button either way, because from the user's point of view it is the same intent.
   *
   * Our own notes are kept on an update: they explain why *we* graded it, and the other site's
   * reasoning isn't ours to overwrite.
   */
  const adopt = (row: MatrixRow, source: IVehicleComplexityRule) => {
    if (!customerId) return;

    const mine = row.byCustomerId[customerId];
    setAdoptingKey(row.key);

    const onSettled = () => setAdoptingKey(null);

    if (mine) {
      updateRule.mutate(
        {
          ruleId: mine.id,
          body: { make: mine.make, model: mine.model, complexity: source.complexity, notes: mine.notes },
        },
        { ...saveHandlers, onSettled }
      );
      return;
    }

    createRule.mutate(
      {
        make: row.make,
        model: row.model,
        complexity: source.complexity,
        notes: t("Adopted from {{site}}", { site: source.customerName ?? t("another site") }),
      },
      { ...saveHandlers, onSettled }
    );
  };

  const handleDelete = () => {
    if (!deleteTarget) return;

    deleteRule.mutate(deleteTarget.id, {
      ...deleteHandlers,
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setDeleteTarget(undefined);
      },
    });
  };

  if (rules.isLoading) {
    return (
      <div className="py-10">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <>
      <VehicleComplexityRuleModal
        open={modalOpen}
        setOpen={setModalOpen}
        rule={editing}
        prefill={prefill}
      />

      <ConfirmModal
        open={!!deleteTarget}
        setOpen={(open: boolean) => !open && setDeleteTarget(undefined)}
        title={t("Delete this rule?")}
        message={t(
          "Vehicles already in the system keep the complexity they were given. This only affects vehicles created from now on."
        )}
        confirmButtonText={t("Delete")}
        onConfirm={handleDelete}
        isLoading={deleteRule.isLoading}
      />

      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <p className="max-w-3xl text-sm text-gray-600">
            {t(
              "Each row is a make and model; each column is a site. Only your own rules are used when grading your vehicles — the other columns are here so you can see how another site grades the same vehicle and copy it across."
            )}
          </p>
          <ButtonInput
            label={t("Add rule")}
            isSubmit={false}
            onClick={() => openCreate()}
            classes={undefined}
          />
        </div>

        {rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-300 p-8 text-center">
            <p className="text-sm text-gray-600">
              {t("No complexity rules yet. Add one, and every vehicle created from then on is graded against it.")}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">{t("Make")}</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">{t("Model")}</th>
                  {customers.map((customer) => (
                    <th
                      key={customer.id}
                      className={`px-4 py-2 text-left text-xs font-semibold ${
                        customer.isMine ? "text-primary-700" : "text-gray-600"
                      }`}
                    >
                      {customer.name}
                    </th>
                  ))}
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">{t("Actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => {
                  const mine = customerId ? row.byCustomerId[customerId] : undefined;

                  return (
                    <tr key={row.key} className="bg-white hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm">
                        {row.make ?? <span className="italic text-gray-400">{t(ANY)}</span>}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {row.model ?? <span className="italic text-gray-400">{t(ANY)}</span>}
                      </td>

                      {customers.map((customer) => {
                        const rule = row.byCustomerId[customer.id];

                        if (!rule) {
                          return (
                            <td key={customer.id} className="px-4 py-2 text-sm text-gray-300">
                              —
                            </td>
                          );
                        }

                        // Nothing to adopt from our own column, and nothing to adopt when the other
                        // site already agrees with us.
                        const canAdopt =
                          !customer.isMine && (!mine || mine.complexity !== rule.complexity);

                        return (
                          <td key={customer.id} className="px-4 py-2 text-sm">
                            <div className="flex items-center gap-2">
                              {/* Only wrap when there are notes — Tooltip always renders its bubble,
                                  so an empty one would follow the cursor around for no reason. */}
                              {rule.notes ? (
                                <Tooltip content={rule.notes}>
                                  <span
                                    className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${
                                      COMPLEXITY_CLASSES[rule.complexity]
                                    }`}
                                  >
                                    {t(COMPLEXITY_LABELS[rule.complexity])}
                                  </span>
                                </Tooltip>
                              ) : (
                                <span
                                  className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${
                                    COMPLEXITY_CLASSES[rule.complexity]
                                  }`}
                                >
                                  {t(COMPLEXITY_LABELS[rule.complexity])}
                                </span>
                              )}

                              {canAdopt && (
                                <button
                                  type="button"
                                  className="rounded p-1 text-gray-400 hover:bg-primary-50 hover:text-primary-600 disabled:opacity-40"
                                  onClick={() => adopt(row, rule)}
                                  disabled={adoptingKey === row.key}
                                  title={
                                    mine
                                      ? t("Change your grading to {{complexity}}", {
                                          complexity: t(COMPLEXITY_LABELS[rule.complexity]),
                                        })
                                      : t("Add this to your site as {{complexity}}", {
                                          complexity: t(COMPLEXITY_LABELS[rule.complexity]),
                                        })
                                  }
                                >
                                  <ArrowDownOnSquareIcon className="h-4 w-4" />
                                  <span className="sr-only">{t("Copy to your site")}</span>
                                </button>
                              )}
                            </div>
                          </td>
                        );
                      })}

                      <td className="px-4 py-2 text-right text-sm">
                        {mine ? (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-primary-600"
                              onClick={() => openEdit(mine)}
                              title={t("Edit your rule")}
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                              <span className="sr-only">{t("Edit")}</span>
                            </button>
                            <button
                              type="button"
                              className="rounded p-1 text-gray-500 hover:bg-red-50 hover:text-red-600"
                              onClick={() => setDeleteTarget(mine)}
                              title={t("Delete your rule")}
                            >
                              <TrashIcon className="h-4 w-4" />
                              <span className="sr-only">{t("Delete")}</span>
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="text-sm font-medium text-primary-600 hover:underline"
                            onClick={() => openCreate({ make: row.make, model: row.model })}
                          >
                            {t("Add yours")}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
